import { useMemo } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { mappingTypeActivities } from '@/modules/common/utils/type-format.ts';
import { DisclosureListItem } from 'ui';
import { fixNameEnum, goodFormatUSDCurrency } from 'ui/utils';
import { ActivitiesPaginateResponse } from 'api';

interface InvestorTransactionItemProps {
  transaction: ActivitiesPaginateResponse;
}

const InvestorTransactionItem = ({ transaction }: InvestorTransactionItemProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const {
    type,
    amount,
    source,
    status,
    quantity,
    payment_id,
    register_id,
    price_per_share,
    completion_date,
    requested_at_date,
    register_class_name,
  } = transaction;

  const bottomContentData = useMemo(
    () => [
      ['Completion date', dateToPrint(completion_date)],
      ['Request date', dateToPrint(requested_at_date)],
      ['Class', register_class_name],
      ['Payment status', fixNameEnum(status)],
      ['Price per share/unit', `${goodFormatUSDCurrency(price_per_share ?? 0)}`],
      ['Payment ID', payment_id ? `#${payment_id}` : '-'],
    ],
    [completion_date, payment_id, price_per_share, register_class_name, requested_at_date, status],
  );

  const name = useMemo(() => {
    return type === 'subscription'
      ? getRegisterTypeStrings('application', { registerId: register_id }) || 'Subscription'
      : mappingTypeActivities(type);
  }, [getRegisterTypeStrings, register_id, type]);

  return (
    <DisclosureListItem
      leftMainString={name}
      leftSubduedContentSet={[
        dateToPrint(completion_date) || '-',
        register_class_name || '-',
        fixNameEnum(source),
      ].filter(item => item !== '-')}
      rightMainString={`${goodFormatUSDCurrency(Math.round(amount * 100) / 100)}`}
      rightSubduedString={`${formatNumberWithCommas(+quantity.toFixed(4))} units/shares`}
      bottomContent={bottomContentData}
    />
  );
};

export default InvestorTransactionItem;
